import { useState } from 'react';
import { CheckCircle2, Plus, Target } from 'lucide-react';
import { TipoAlvo, TipoAtividade, formatarDiaMes, type AtividadeDto } from '@nexital/shared';
import { progressoPassos } from '../../domain/licitacao';
import { Avatar } from '../design-system/Avatar';
import { useLicitacao } from './contexto';
import { DialogoAtividade } from './DialogoAtividade';
import { Modulo } from './Modulo';
import { PontaItem, atributosAlvo } from './PontaItem';

/** Lista vazia fixa: o seletor do store não pode devolver uma lista nova a cada leitura (laço infinito no React). */
const NENHUM: never[] = [];

const RAIO = 34;
const CIRCUNFERENCIA = 2 * Math.PI * RAIO;

/** Anel "N de M" calculado: passos concluídos sobre o total (adaptação aprovada da referência B). */
function Anel({ concluidos, total }: { concluidos: number; total: number }) {
  const fracao = total ? concluidos / total : 0;
  return (
    <svg className="passos__anel" viewBox="0 0 80 80" role="img" aria-label={`${concluidos} de ${total} passos concluídos`}>
      <circle cx="40" cy="40" r={RAIO} className="passos__anel-fundo" />
      <circle cx="40" cy="40" r={RAIO} className="passos__anel-progresso" strokeDasharray={CIRCUNFERENCIA} strokeDashoffset={CIRCUNFERENCIA * (1 - fracao)} />
      <text x="40" y="45" textAnchor="middle">{`${concluidos} de ${total}`}</text>
    </svg>
  );
}

const hora = (iso: string) => new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

function Passo({ passo, numero }: { passo: AtividadeDto; numero: number }) {
  const alterar = useLicitacao((s) => s.alterarAtividade);
  const atrasado = !!passo.prazo && new Date(passo.prazo).getTime() < Date.now();
  return (
    <li className={`passo alvo-item${atrasado ? ' passo--urgente' : ''}`} {...atributosAlvo(TipoAlvo.Atividade, passo.uuid)}>
      <PontaItem uuid={passo.uuid} />
      <span className="passo__numero" aria-hidden>{numero}</span>
      <div className="passo__dados">
        <strong>{passo.titulo}</strong>
        <span className="passo__detalhe">
          {passo.responsavel && <Avatar nome={passo.responsavel.nome} chave={passo.responsavel.uuid} tamanho={22} />}
          {passo.responsavel?.nome.split(' ')[0]}
          {passo.prazo && ` • até ${formatarDiaMes(passo.prazo)}, ${hora(passo.prazo)}`}
        </span>
      </div>
      <input
        type="checkbox"
        className="passo__concluir"
        checked={false}
        onChange={() => void alterar(passo.uuid, { concluida: true })}
        aria-label={`Concluir "${passo.titulo}"`}
      />
    </li>
  );
}

/** "Próximos passos" (RQ-LIC-002): o que falta fazer, por prazo, com o anel de progresso. */
export function ModuloPassos() {
  const atividades = useLicitacao((s) => s.dados?.atividades ?? NENHUM);
  const etapa = useLicitacao((s) => s.dados?.etapas.find((e) => e.uuid === s.dados?.processo.estadoUuid)?.nome ?? 'Etapa atual');
  const [novo, definirNovo] = useState(false);
  const passos = atividades.filter((a) => a.tipo !== TipoAtividade.Marco);
  const pendentes = passos.filter((a) => !a.concluidaEm);
  const concluidos = passos.length - pendentes.length;
  const acoes = (
    <button type="button" className="botao-icone" onClick={() => definirNovo(true)} aria-label="Adicionar passo">
      <Plus size={18} />
    </button>
  );
  return (
    <Modulo titulo="Próximos passos" Icone={Target} cor="branco" acoes={acoes}>
      <div className="passos__resumo">
        <Anel {...progressoPassos(atividades)} />
        <div>
          <strong>{etapa}</strong>
          <p>{pendentes.length ? 'Conclua os próximos passos para avançar.' : 'Nenhum passo pendente.'}</p>
        </div>
      </div>
      <ol className="passos">
        {pendentes.map((p, i) => <Passo key={p.uuid} passo={p} numero={i + 1} />)}
      </ol>
      {concluidos > 0 && (
        <p className="passos__concluidos">
          <CheckCircle2 size={18} aria-hidden /> {concluidos === 1 ? '1 passo concluído' : `${concluidos} passos concluídos`}
        </p>
      )}
      {novo && <DialogoAtividade tipo={TipoAtividade.Atividade} fechar={() => definirNovo(false)} />}
    </Modulo>
  );
}
